import React, { Component } from 'react';
import { X, Send, Heart, Trash2, Reply } from 'lucide-react';
import apiService from '@/services/api';
import styles from './CommentsSection.module.scss';

interface Comment {
  id: string;
  contentText: string;
  author: {
    id: string;
    displayName: string;
    profilePic?: string;
  };
  likesCount: number;
  isLiked: boolean;
  createdAt: string;
  parentCommentId?: string | null;
  parentComment?: {
    contentText: string;
    author: {
      displayName: string;
      profilePic?: string;
    };
  };
  replies?: Comment[];
}

interface CommentsSectionProps {
  postId: string;
  onClose: () => void;
}

interface CommentsSectionState {
  comments: Comment[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  newComment: string;
  isSubmitting: boolean;
  error: string | null;
  replyingToCommentId: string | null;
  replyingToAuthor: string | null;
  replyText: string;
}

export class CommentsSection extends Component<CommentsSectionProps, CommentsSectionState> {
  private commentInputRef: React.RefObject<HTMLTextAreaElement> = React.createRef();

  constructor(props: CommentsSectionProps) {
    super(props);
    this.state = {
      comments: [],
      loading: true,
      loadingMore: false,
      hasMore: true,
      currentPage: 1,
      newComment: '',
      isSubmitting: false,
      error: null,
      replyingToCommentId: null,
      replyingToAuthor: null,
      replyText: '',
    };
  }

  componentDidMount() {
    this.loadComments();
    document.body.style.overflow = 'hidden';
  }

  componentWillUnmount() {
    document.body.style.overflow = 'unset';
  }

  loadComments = async (page: number = 1) => {
    this.setState({
      loading: page === 1,
      loadingMore: page > 1,
      error: null,
    });

    try {
      const response = await apiService.getSocialPostComments(this.props.postId, page, 50);

      if (response.success && response.data) {
        const rawComments = response.data.comments || [];
        const pagination = response.data.pagination || {};

        // Flatten all comments (including nested replies) into a single array
        const flattenComments = (comments: any[]): Comment[] => {
          const flatList: Comment[] = [];
          const commentMap = new Map<string, Comment>(); // Map to store all comments by ID
          
          // First pass: collect all comments (top-level and nested) into a map
          const collectComments = (commentList: any[]) => {
            commentList.forEach((comment: any) => {
              const mappedComment: Comment = {
                id: comment.id,
                contentText: comment.commentText || comment.contentText || '',
                author: {
                  id: comment.commenter?.id || comment.author?.id || '',
                  displayName: comment.commenter?.displayName || comment.author?.displayName || 'Unknown',
                  profilePic: comment.commenter?.profilePic || comment.author?.profilePic,
                },
                likesCount: comment.likesCount || 0,
                isLiked: comment.userReaction === 'like' || comment.isLiked || false,
                createdAt: comment.createdAt,
                parentCommentId: comment.parentCommentId || null,
              };
              
              commentMap.set(comment.id, mappedComment);
              
              // Recursively process nested replies
              if (comment.replies && comment.replies.length > 0) {
                collectComments(comment.replies);
              }
            });
          };
          
          collectComments(comments);
          
          // Second pass: build flat list and attach parent comment info
          commentMap.forEach((comment) => {
            // If it's a reply, find and attach parent comment info
            if (comment.parentCommentId) {
              const parentComment = commentMap.get(comment.parentCommentId);
              if (parentComment) {
                comment.parentComment = {
                  contentText: parentComment.contentText,
                  author: {
                    displayName: parentComment.author.displayName,
                    profilePic: parentComment.author.profilePic,
                  },
                };
              }
            }
            
            // Add to flat list
            flatList.push(comment);
          });
          
          return flatList;
        };

        // Flatten and sort comments by creation date (oldest first, so latest at bottom)
        const flattenedComments = flattenComments(rawComments);
        flattenedComments.sort((a, b) => {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });

        this.setState({
          comments: page === 1 ? flattenedComments : [...this.state.comments, ...flattenedComments],
          hasMore: pagination.hasMore !== false,
          currentPage: page,
          loading: false,
          loadingMore: false,
        });
      } else {
        throw new Error(response.message || 'Failed to load comments');
      }
    } catch (error: any) {
      console.error('Failed to load comments:', error);
      this.setState({
        error: error.message || 'Failed to load comments',
        loading: false,
        loadingMore: false,
      });
    }
  };

  handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { newComment, replyingToCommentId, replyText } = this.state;
    const commentText = replyingToCommentId ? replyText.trim() : newComment.trim();
    
    if (!commentText || this.state.isSubmitting) return;

    this.setState({ isSubmitting: true, error: null });

    try {
      // Prepare request body according to API spec
      const requestBody: {
        commentText: string;
        parentCommentId: string | null;
      } = {
        commentText: commentText,
        parentCommentId: replyingToCommentId || null, // null for top-level comments, UUID for replies
      };

      const response = await apiService.createSocialPostComment(this.props.postId, requestBody);

      if (response.success && response.data) {
        const newCommentData = response.data;
        
        // Map the new comment/reply
        const mappedComment: Comment = {
          id: newCommentData.id,
          contentText: newCommentData.commentText || '',
          author: {
            id: newCommentData.commenter?.id || '',
            displayName: newCommentData.commenter?.displayName || 'Unknown',
            profilePic: newCommentData.commenter?.profilePic,
          },
          likesCount: newCommentData.likesCount || 0,
          isLiked: newCommentData.userReaction === 'like' || false,
          createdAt: newCommentData.createdAt,
          parentCommentId: newCommentData.parentCommentId || null,
        };
        
        // If it's a reply, find and attach parent comment info
        if (newCommentData.parentCommentId) {
          const parentComment = this.state.comments.find(c => c.id === newCommentData.parentCommentId);
          if (parentComment) {
            mappedComment.parentComment = {
              contentText: parentComment.contentText,
              author: {
                displayName: parentComment.author.displayName,
                profilePic: parentComment.author.profilePic,
              },
            };
          }
        }
        
        // Add to comments list and sort by date (oldest first, latest at bottom)
        const updatedComments = [...this.state.comments, mappedComment].sort((a, b) => {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
        
        this.setState({
          comments: updatedComments,
          newComment: '',
          replyingToCommentId: null,
          replyingToAuthor: null,
          replyText: '',
        });
      } else {
        throw new Error(response.message || 'Failed to post comment');
      }
    } catch (error: any) {
      console.error('Failed to post comment:', error);
      this.setState({
        error: error.message || 'Failed to post comment. Please try again.',
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleReplyClick = (commentId: string, authorName: string) => {
    this.setState({
      replyingToCommentId: commentId,
      replyingToAuthor: authorName,
      replyText: '',
    });
    // Focus on reply input after a short delay
    setTimeout(() => {
      if (this.commentInputRef.current) {
        this.commentInputRef.current.focus();
      }
    }, 100);
  };

  handleCancelReply = () => {
    this.setState({
      replyingToCommentId: null,
      replyingToAuthor: null,
      replyText: '',
    });
  };

  handleLikeComment = async (commentId: string) => {
    // TODO: Implement like comment API call
    console.log('Like comment:', commentId);
  };

  handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await apiService.deleteSocialPostComment(this.props.postId, commentId);

      if (response.success) {
        // Remove comment from state (works for both top-level and replies in flat list)
        this.setState({
          comments: this.state.comments.filter(c => c.id !== commentId),
        });
      } else {
        throw new Error(response.message || 'Failed to delete comment');
      }
    } catch (error: any) {
      console.error('Failed to delete comment:', error);
      alert(error.message || 'Failed to delete comment');
    }
  };

  formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return `${days}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  scrollToComment = (commentId: string) => {
    // Find the comment element by ID
    const commentElement = document.querySelector(`[data-comment-id="${commentId}"]`);
    if (commentElement) {
      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Add highlight effect
      commentElement.classList.add(styles.highlighted);
      setTimeout(() => {
        commentElement.classList.remove(styles.highlighted);
      }, 2000);
    }
  };

  renderComment = (comment: Comment) => {
    const isReply = !!comment.parentCommentId;
    
    return (
      <div 
        key={comment.id} 
        data-comment-id={comment.id}
        className={`${styles.comment} ${isReply ? styles.replyComment : ''}`}
      >
        <img
          src={comment.author.profilePic || '/default-avatar.png'}
          alt={comment.author.displayName}
          className={styles.commentAvatar}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/default-avatar.png';
          }}
        />
        <div className={styles.commentContent}>
          {/* Show parent comment context if this is a reply */}
          {comment.parentComment && comment.parentCommentId && (
            <div 
              className={styles.parentCommentContext}
              onClick={() => this.scrollToComment(comment.parentCommentId!)}
              title="Click to scroll to parent comment"
            >
              <div className={styles.parentCommentHeader}>
                <img
                  src={comment.parentComment.author.profilePic || '/default-avatar.png'}
                  alt={comment.parentComment.author.displayName}
                  className={styles.parentCommentAvatar}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/default-avatar.png';
                  }}
                />
                <span className={styles.parentCommentAuthor}>{comment.parentComment.author.displayName}</span>
              </div>
              <p className={styles.parentCommentText}>{comment.parentComment.contentText}</p>
            </div>
          )}
          
          <div className={styles.commentHeader}>
            <span className={styles.commentAuthor}>{comment.author.displayName}</span>
            <span className={styles.commentDate}>{this.formatDate(comment.createdAt)}</span>
          </div>
          <p className={styles.commentText}>{comment.contentText}</p>
          <div className={styles.commentActions}>
            <button
              className={`${styles.commentActionButton} ${comment.isLiked ? styles.active : ''}`}
              onClick={() => this.handleLikeComment(comment.id)}
              title="Like comment"
            >
              <Heart size={16} fill={comment.isLiked ? 'currentColor' : 'none'} />
              <span>{comment.likesCount || 0}</span>
            </button>
            {/* Every comment (both top-level and replies) can be replied to */}
            <button
              className={styles.commentActionButton}
              onClick={() => this.handleReplyClick(comment.id, comment.author.displayName)}
              title="Reply to comment"
            >
              <Reply size={16} />
              <span>Reply</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      comments,
      loading,
      loadingMore,
      hasMore,
      newComment,
      isSubmitting,
      error,
    } = this.state;

    return (
      <div className={styles.overlay} onClick={this.props.onClose}>
        <div className={styles.commentsPanel} onClick={(e) => e.stopPropagation()}>
          <div className={styles.commentsHeader}>
            <h2 className={styles.commentsTitle}>Comments</h2>
            <button className={styles.closeButton} onClick={this.props.onClose} title="Close">
              <X size={24} />
            </button>
          </div>

          <div className={styles.commentsList}>
            {loading && comments.length === 0 ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Loading comments...</p>
              </div>
            ) : error && comments.length === 0 ? (
              <div className={styles.errorState}>
                <p>{error}</p>
                <button onClick={() => this.loadComments(1)} className={styles.retryButton}>
                  Retry
                </button>
              </div>
            ) : comments.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No comments yet. Be the first to comment!</p>
              </div>
            ) : (
              <>
                {comments.map(comment => this.renderComment(comment))}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <div className={styles.spinner}></div>
                  </div>
                )}
                {hasMore && !loadingMore && (
                  <button
                    className={styles.loadMoreButton}
                    onClick={() => this.loadComments(this.state.currentPage + 1)}
                  >
                    Load more comments
                  </button>
                )}
              </>
            )}
          </div>

          <form className={styles.commentForm} onSubmit={this.handleSubmitComment}>
            {error && (
              <div className={styles.errorMessage}>{error}</div>
            )}
            {this.state.replyingToCommentId && (
              <div className={styles.replyingTo}>
                <span className={styles.replyingToText}>
                  Replying to <strong>{this.state.replyingToAuthor}</strong>
                </span>
                <button
                  type="button"
                  className={styles.cancelReplyButton}
                  onClick={this.handleCancelReply}
                  title="Cancel reply"
                >
                  <X size={16} />
                </button>
              </div>
            )}
            <div className={styles.commentInputContainer}>
              <textarea
                ref={this.commentInputRef}
                className={styles.commentInput}
                placeholder={this.state.replyingToCommentId ? `Reply to ${this.state.replyingToAuthor}...` : "Write a comment..."}
                value={this.state.replyingToCommentId ? this.state.replyText : newComment}
                onChange={(e) => {
                  if (this.state.replyingToCommentId) {
                    this.setState({ replyText: e.target.value });
                  } else {
                    this.setState({ newComment: e.target.value });
                  }
                }}
                rows={3}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className={styles.submitButton}
                disabled={
                  (this.state.replyingToCommentId 
                    ? !this.state.replyText.trim() 
                    : !newComment.trim()) || isSubmitting
                }
                title={this.state.replyingToCommentId ? "Post reply" : "Post comment"}
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default CommentsSection;

